import { useQuery, useQueryClient } from "@tanstack/solid-query";
import type { BenchmarkResponse } from "contracts/api/benchmark/responses";
import type { WebsiteHealthSummary } from "contracts/api/website/responses";
import { createEffect, createSignal, For, Show } from "solid-js";
import { commaSeparatedEntered } from "../../boundaries/user-list-input.parser";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";

// Admin editing of a website's peer-benchmark distribution. Benchmarks are
// report-scoped with no automated producer, so the panel edits the latest
// completed report; a website with no report yet must be scanned first.
export function BenchmarkEditor(props: { websites: WebsiteHealthSummary[] }) {
  const [websiteId, setWebsiteId] = createSignal(
    props.websites[0]?.websiteId ?? "",
  );

  const benchmark = useQuery(() => ({
    queryKey: ["benchmark", websiteId()],
    queryFn: () => api.readBenchmark(websiteId()),
    enabled: websiteId() !== "",
  }));

  return (
    <section class="admin-section">
      <h4 class="admin-section-title">Peer benchmark</h4>
      <Show
        when={props.websites.length > 0}
        fallback={<div class="empty-title">No websites to benchmark yet</div>}
      >
        <label class="editor-field">
          <span class="editor-field-label">Website</span>
          <select
            class="editor-input"
            value={websiteId()}
            onChange={(event) => setWebsiteId(event.currentTarget.value)}
          >
            <For each={props.websites}>
              {(site) => (
                <option value={site.websiteId}>
                  {site.displayName ?? site.clientName} · {site.domain}
                </option>
              )}
            </For>
          </select>
        </label>
        <Show
          when={benchmark.data}
          fallback={<div class="loading">Loading benchmark…</div>}
        >
          {(data) => (
            <Show
              when={data().hasReport}
              fallback={
                <div class="empty-title">
                  Scan this website first — benchmarks attach to a completed
                  report.
                </div>
              }
            >
              <BenchmarkForm websiteId={websiteId()} data={data()} />
            </Show>
          )}
        </Show>
      </Show>
    </section>
  );
}

function BenchmarkForm(props: { websiteId: string; data: BenchmarkResponse }) {
  const queryClient = useQueryClient();
  const [target, setTarget] = createSignal("");
  const [peerMedian, setPeerMedian] = createSignal("");
  const [percentile, setPercentile] = createSignal("");
  const [distribution, setDistribution] = createSignal("");
  const action = createAsyncAction((cause) =>
    cause instanceof Error ? cause.message : "Save failed",
  );

  createEffect(() => {
    const current = props.data.benchmark;
    setTarget(current?.target?.toString() ?? "");
    setPeerMedian(current?.peerMedian?.toString() ?? "");
    setPercentile(current?.percentile?.toString() ?? "");
    setDistribution(current?.distribution.join(", ") ?? "");
  });

  const save = () =>
    action.run(async () => {
      await api.upsertBenchmark(props.websiteId, {
        target: nullableScore(target()),
        peerMedian: nullableScore(peerMedian()),
        percentile: nullableScore(percentile()),
        distribution: commaSeparatedEntered(distribution()).map((value) =>
          Math.max(0, Number.parseInt(value, 10) || 0),
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: ["benchmark", props.websiteId],
      });
    });

  return (
    <div class="editor">
      <ScoreField label="Target" value={target()} onChange={setTarget} />
      <ScoreField
        label="Peer median"
        value={peerMedian()}
        onChange={setPeerMedian}
      />
      <ScoreField
        label="Percentile"
        value={percentile()}
        onChange={setPercentile}
      />
      <label class="editor-field">
        <span class="editor-field-label">Distribution (comma-separated)</span>
        <input
          class="editor-input mono"
          value={distribution()}
          placeholder="4, 9, 15, 22, 18, 10, 3"
          onInput={(event) => setDistribution(event.currentTarget.value)}
        />
      </label>
      <div class="editor-foot">
        <Show when={action.error()}>
          <span class="gate-error">{action.error()}</span>
        </Show>
        <button
          class="btn btn-primary"
          type="button"
          disabled={action.busy()}
          onClick={save}
        >
          {action.busy() ? "Saving…" : "Save benchmark"}
        </button>
      </div>
    </div>
  );
}

function ScoreField(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label class="editor-field">
      <span class="editor-field-label">{props.label}</span>
      <input
        class="editor-input mono"
        type="number"
        min="0"
        max="100"
        value={props.value}
        onInput={(event) => props.onChange(event.currentTarget.value)}
      />
    </label>
  );
}

function nullableScore(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  return Math.max(0, Math.min(100, Number.parseInt(trimmed, 10) || 0));
}
